'use client';

import { Table } from 'antd';
import type { TableProps } from 'antd';
import { useCallback, useMemo, useRef, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import {
  mergeEvimoTablePagination,
  type EvimoPaginationLiveState,
} from '~/app/components/common/EvimoTable/evimoTablePagination';
import {
  estimateEvimoTableBodyScrollYFallback,
  type ComputeEvimoTableBodyScrollYOptions,
} from '~/app/components/common/EvimoTable/computeEvimoTableBodyScrollY';
import { useEvimoTableBodyScrollY } from '~/app/components/common/EvimoTable/hooks/useEvimoTableBodyScrollY';
import { prepareEvimoTableColumns } from '~/app/components/common/EvimoTable/prepareEvimoTableColumns';

export type EvimoTableVariant = 'default' | 'flush' | 'technical';

/** Row/cell padding preset. `dense` matches v2 list tables; `standard` is roomier. */
export type EvimoTableDensity = 'dense' | 'standard';

export type EvimoTableProps<T extends object = object> = TableProps<T> & {
  /** @default 'default' */
  variant?: EvimoTableVariant;
  /** @default 'dense' */
  density?: EvimoTableDensity;
  /**
   * Keeps the last column visible while horizontally scrolling (CSS sticky, not Ant
   * `fixed`). When the last column uses `fixed: 'right'`, EvimoTable converts it
   * automatically to avoid overlapping header cells.
   */
  stickyLastColumn?: boolean;
  /**
   * When true (default for wrapped tables), tbody scrolls vertically and pagination stays visible.
   * Set false in compact modals or when `scroll.y` is managed manually.
   * @default true
   */
  scrollBody?: boolean;
  /**
   * Viewport tbody scroll when `pagination={false}` (infinite-scroll lists).
   * @default false
   */
  scrollBodyWhenUnpaginated?: boolean;
  /** Optional ref to the outer `.evimo-table-wrap` (e.g. infinite-scroll binding). */
  tableWrapRef?: RefObject<HTMLDivElement | null>;
  /** Passed to viewport tbody height measurement (`scroll.y`). */
  scrollBodyComputeOptions?: ComputeEvimoTableBodyScrollYOptions;
};

function tableClassName(
  variant: EvimoTableVariant,
  density: EvimoTableDensity,
  stickyLastColumn: boolean,
  className?: string,
): string {
  return [
    'evimo-table',
    'ds-table',
    density === 'standard' ? 'ds-table--standard' : 'ds-table--dense',
    variant === 'technical' ? 'ds-table--technical' : '',
    stickyLastColumn ? 'evimo-table--sticky-last-col' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');
}

function paginationMeasureKey(
  pagination: TableProps['pagination'],
  dataSource: TableProps['dataSource'],
): string {
  if (pagination === false) {
    return 'none';
  }
  const config = pagination ?? {};
  const pageSize = config.pageSize ?? 10;
  const current = config.current ?? 1;
  const total = config.total ?? '—';
  const rows = Array.isArray(dataSource) ? dataSource.length : 0;
  return `${pageSize}-${current}-${total}-${rows}`;
}

/**
 * Dense list table aligned with docs/design/evimo-incidents_v2.html (.tbl).
 * Wraps ant Design Table with shared typography, borders, and row hover.
 * Pair with {@link FilterBar} inside {@link EvimoListPanel} for the v2 list card.
 */
export function EvimoTable<T extends object = object>({
  variant = 'default',
  density = 'dense',
  stickyLastColumn = false,
  scrollBody = true,
  scrollBodyWhenUnpaginated = false,
  tableWrapRef,
  scrollBodyComputeOptions,
  className,
  size = 'small',
  tableLayout = 'auto',
  pagination,
  scroll,
  dataSource,
  columns,
  ...tableProps
}: EvimoTableProps<T>) {
  const { t, i18n } = useTranslation('common');
  const wrapRef = useRef<HTMLDivElement>(null);
  const assignWrapRef = useCallback(
    (node: HTMLDivElement | null) => {
      wrapRef.current = node;
      if (tableWrapRef) {
        tableWrapRef.current = node;
      }
    },
    [tableWrapRef],
  );
  const paginationLiveState = useRef<EvimoPaginationLiveState>({
    pageSize: 10,
    current: 1,
  });

  const mergedPagination = useMemo(
    () => mergeEvimoTablePagination(pagination, t, paginationLiveState),
    [pagination, t, i18n.language],
  );

  const scrollBodyEnabled =
    scrollBody &&
    variant !== 'flush' &&
    scroll?.y === undefined &&
    (mergedPagination !== false || scrollBodyWhenUnpaginated);

  const measureKey = paginationMeasureKey(pagination, dataSource);

  const bodyScrollY = useEvimoTableBodyScrollY(wrapRef, {
    enabled: scrollBodyEnabled,
    measureKey,
    computeOptions: scrollBodyComputeOptions,
  });

  const effectiveBodyScrollY = useMemo(() => {
    if (!scrollBodyEnabled) {
      return undefined;
    }
    return bodyScrollY ?? estimateEvimoTableBodyScrollYFallback();
  }, [scrollBodyEnabled, bodyScrollY]);

  const { columns: resolvedColumns, stickyLastColumn: stickyLastCol } = useMemo(
    () => prepareEvimoTableColumns(columns, stickyLastColumn),
    [columns, stickyLastColumn],
  );

  const mergedScroll = useMemo(() => {
    let next = scroll;
    if (stickyLastCol && next?.x === undefined) {
      next = { ...next, x: 'max-content' };
    }
    if (!scrollBodyEnabled || effectiveBodyScrollY == null) {
      return next;
    }
    return { ...next, y: effectiveBodyScrollY };
  }, [scroll, scrollBodyEnabled, effectiveBodyScrollY, stickyLastCol]);

  const table = (
    <Table<T>
      {...tableProps}
      columns={resolvedColumns}
      dataSource={dataSource}
      pagination={mergedPagination}
      scroll={mergedScroll}
      size={size}
      tableLayout={tableLayout}
      className={tableClassName(variant, density, stickyLastCol, className)}
    />
  );

  if (variant === 'flush') {
    return table;
  }

  const wrapClassName = [
    'evimo-table-wrap',
    scrollBodyEnabled ? 'evimo-table-wrap--scroll-body' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div ref={assignWrapRef} className={wrapClassName}>
      {table}
    </div>
  );
}
